import { useAuthenticationFirewallMetrics } from './hooks/useAuthenticationFirewallMetrics';
import { QueryFilters, useLastSync, useConfiguration } from './api';
import { Alert, BarChart, Button, ButtonStyles, Icon, IconButton, IconButtonSizes, IconButtonStyles, Icons, LayoutCarousel, SingleMetric, Tooltip } from '@silverfort/sf-components';
import AuthenticationTypeChart from './components/AuthenticationTypeChart/AuthenticationTypeChart';
import ProtocolTypeChart from './components/ProtocolTypeChart/ProtocolTypeChart';
import PolicyTypeChart from './components/PolicyTypeChart/PolicyTypeChart';
import Filters from './components/Filters/Filters';
import { useEffect, useRef, useState } from 'react';
import AuthenticationsTrafficLayout from './components/AuthenticationsTrafficLayout/AuthenticationsTrafficLayout';
import { useAuthenticationsTrafficBarChart } from './hooks/useAuthenticationsTrafficBarChart';
import InvestigationDrawer from '../../components/InvestigationDrawer/InvestigationDrawer';
import { useAppDrawer } from '../../components/AppDrawer/AppDrawerProvider';
import TopActiveChart from './components/TopActiveChart/TopActiveChart';
import TopBlockChart from './components/TopBlockChart/TopBlockChart';

import TopRiskEntityChart from '../../components/TopRiskEntityChart/TopRiskEntityChart';
import TopRiskIndicatorChart from '../../components/TopRiskIndicatorChart/TopRiskIndicatorChart';
import DenyPolicyCarousel from './components/DenyPolicyCarousel/DenyPolicyCarousel';
import { PolicyCountByStatus } from './types';
import LogsDrawer from '../../containers/LogsDrawer/LogsDrawer';
import { LogsFilters } from '../../containers/LogsDrawer/types';
import Insights from './components/Insights/Insights';
import useAuthenticationFirewallDashboardToolbarActions from './hooks/useAuthenticationFirewallDashboardToolbarActions';
import { AD_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { useTowerLayout } from '../../TowerLayout/TowerLayoutProvider';
import Disclaimers from './components/Disclaimers/Disclaimers';
import { formatAuthTrafficShortDates } from '../universalMFA/utils/utils';
import styles from './AuthenticationFirewallDashboard.module.scss';
import { DashboardUtils } from '../../utils/policies';

const AUTH_TRAFFIC_LABELS_LENGTH_THRESHOLD = 30;
const EXCLUDED_CAPTURE_CLASS_NAME = 'exclude-from-capture';

const AuthenticationFirewallDashboard = () => {
    const { displayContent } = useAppDrawer();
    const { setLayoutContent } = useTowerLayout();

    const chartContainerRef = useRef<HTMLDivElement>(null);
    const captureContainerRef = useRef<HTMLDivElement>(null);
    const captureFixedHeaderContainerRef = useRef<HTMLDivElement>(null);
    const captureScrollContainerRef = useRef<HTMLDivElement>(null);

    const [filters, setFilters] = useState<QueryFilters>({ domain: [], timeframe: '30' } as QueryFilters);
    const [chartWidth, setChartWidth] = useState(0);
    const [chartShouldBlink, setChartShouldBlink] = useState(false);
    const [denyPolicyStatus, setDenyPolicyStatus] = useState<keyof PolicyCountByStatus>('total');
    const blinkChartContainerClassName = `transition-colors duration-200 ${chartShouldBlink ? '[&]:bg-[#F5FAFF]' : 'bg-sf-white'}`;

    const { data: lastSync } = useLastSync();
    const { data: configuration } = useConfiguration();

    const handleFiltersChange = (nextFilters: QueryFilters) => {
        if (nextFilters.timeframe !== filters.timeframe) {
            setChartShouldBlink(true);
            setTimeout(() => {
                setChartShouldBlink(false);
            }, 2000);
        }
        setFilters(nextFilters);
    };

    const handleInvestigationClick = (guid: string, adType: number) => {
        displayContent(<InvestigationDrawer guid={guid} adType={adType} />);
    };

    const handleUsersInvestigation = (guid: string) => {
        handleInvestigationClick(guid, AD_ENTITY_TYPES.USER);
    };

    const handleResourcesInvestigation = (guid: string) => {
        handleInvestigationClick(guid, AD_ENTITY_TYPES.SERVER);
    };

    const {
        selectedTab: authenticationsTrafficSelectedTab,
        setSelectedTab: setAuthenticationsTrafficSelectedTab,
        barChartData,
        isLoading: barChartIsLoading,
        barChartVariant,
        mappingList
    } = useAuthenticationsTrafficBarChart({
        filters
    });

    const {
        generateAndDownloadAuthenticationFirewallMetricsFile,
        isGenerateAndDownloadAuthenticationFirewallMetricsFileLoading,
        generateAndDownloadAuthenticationFirewallCapture,
        isGenerateAndDownloadAuthenticationFirewallCaptureLoading
    } = useAuthenticationFirewallDashboardToolbarActions({
        filters,
        captureContainerRef,
        captureFixedHeaderContainerRef,
        captureScrollContainerRef
    });

    const goToPolicyPage = () => {
        window.open('/Policy', '_blank');
    };

    const goToPolicyPageCreatePolicy = () => {
        window.open('/policy#create', '_blank');
    };

    const {
        protectedUsers,
        protectedUsersIsLoading,
        denyPolicies,
        denyPoliciesIsLoading,
        authenticationsDenied,
        authenticationsDeniedIsLoading,
        authenticationDenyRate,
        authenticationDenyRateIsLoading,
        authenticationTypeSummary,
        authenticationTypeSummaryIsLoading,
        protocolTypeSummary,
        protocolTypeSummaryIsLoading,
        policyType,
        policyTypeIsLoading,
        policyTypeIsError,
        activeUsers,
        activeUsersIsLoading,
        activeResources,
        activeResourcesIsLoading,
        blockedResources,
        blockedResourcesIsLoading,
        blockedUsers,
        blockedUsersIsLoading,
        riskyUsers = [],
        riskyUsersIsLoading,
        riskyResources = [],
        riskyResourcesIsLoading,
        riskyIndicators = [],
        riskyIndicatorsIsLoading
    } = useAuthenticationFirewallMetrics(filters);

    const isLoading =
        protectedUsersIsLoading ||
        denyPoliciesIsLoading ||
        authenticationsDeniedIsLoading ||
        authenticationDenyRateIsLoading ||
        authenticationTypeSummaryIsLoading ||
        protocolTypeSummaryIsLoading ||
        policyTypeIsLoading ||
        activeUsersIsLoading ||
        activeResourcesIsLoading ||
        blockedResourcesIsLoading ||
        blockedUsersIsLoading ||
        riskyUsersIsLoading ||
        riskyResourcesIsLoading ||
        riskyIndicatorsIsLoading;

    const getLogsMaxEndDate = (): Date | undefined => {
        if (!lastSync) {
            return;
        }
        const lastSyncLessOneMillisecond = new Date(lastSync.getTime() - 1);
        return lastSyncLessOneMillisecond;
    };

    const openLogsDrawer = (logsFilters: LogsFilters) => {
        const logsFiltersConfig = DashboardUtils.getLogsFiltersFromDefaultAuthLogsFilters(
            configuration?.authFilters,
            logsFilters
        );

        displayContent(
            <LogsDrawer
                filters={{
                    ...logsFilters,
                    action: 'deny',
                    domain: filters.domain,
                    timeframe: filters.timeframe,
                    ...logsFiltersConfig.filters
                }}
                excludedFilters={logsFiltersConfig.excludedFilters}
                logsTableTitle="Authentications blocked"
                maxEndDate={getLogsMaxEndDate()}
            />
        );
    };

    useEffect(() => {
        const updateWidth = () => {
            if (chartContainerRef.current) {
                setChartWidth(chartContainerRef.current.offsetWidth);
            }
        };

        updateWidth();
        window.addEventListener('resize', updateWidth);

        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    useEffect(() => {
        setLayoutContent(<Disclaimers />);
    }, [setLayoutContent]);

    return (
        <>
            <div id="wrapper" className="h-[calc(100vh-64px)] flex justify-center mt-[64px] p-0 bg-sf-white">
                <div
                    ref={captureContainerRef}
                    className="flex flex-col max-w-full 2xl:max-w-[1400px] 3xl:max-w-[1800px] bg-sf-gray-50 border-s border-sf-gray-100 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                    <div
                        ref={captureFixedHeaderContainerRef}
                        className="w-full flex flex-row justify-between gap-6 px-6 pt-6 pb-4 bg-sf-gray-50 sticky top-0 z-10">
                        <Filters
                            filters={filters}
                            onFiltersChange={handleFiltersChange}
                            excludedCaptureClassName={EXCLUDED_CAPTURE_CLASS_NAME}
                            lastSync={lastSync ?? undefined}
                        />
                        <div className={`flex flex-row gap-2 ${EXCLUDED_CAPTURE_CLASS_NAME}`}>
                            <Tooltip title="Export CSV">
                                <Button
                                    iconRight={Icons.Download}
                                    buttonStyle={ButtonStyles.Primary}
                                    title="Export CSV"
                                    disabled={isLoading}
                                    isLoading={isGenerateAndDownloadAuthenticationFirewallMetricsFileLoading}
                                    onClick={generateAndDownloadAuthenticationFirewallMetricsFile}
                                />
                            </Tooltip>
                            <Tooltip title="Capture">
                                <Button
                                    iconRight={Icons.Capture}
                                    buttonStyle={ButtonStyles.Primary}
                                    title="Capture"
                                    disabled={isLoading}
                                    onClick={() =>
                                        generateAndDownloadAuthenticationFirewallCapture([EXCLUDED_CAPTURE_CLASS_NAME])
                                    }
                                    isLoading={isGenerateAndDownloadAuthenticationFirewallCaptureLoading}
                                />
                            </Tooltip>
                            <Button
                                iconLeft={Icons.Add}
                                buttonStyle={ButtonStyles.PrimarySolid}
                                disabled={isLoading}
                                onClick={() => goToPolicyPageCreatePolicy()}
                                className='text-nowrap'
                                text='Create Policy'
                            />
                        </div>
                    </div>
                    <div ref={captureScrollContainerRef} className="flex-1 px-6 pb-6">
                        {/* CSS Grid Layout for stable chart sizing */}
                        <div className={styles.dashboardGrid}>
                            {/* Deny policies Section */}
                            <div className="h-[94px] min-h-[94px]" style={{ gridArea: 'policies' }}>
                                <DenyPolicyCarousel
                                    title="Deny policies"
                                    policies={denyPolicies}
                                    isLoading={denyPoliciesIsLoading}
                                    selectedStatus={denyPolicyStatus}
                                    onStatusChange={setDenyPolicyStatus}
                                    onIconClick={goToPolicyPage}
                                />
                            </div>
                            {/* Protected Users Metric Card */}
                            <div className="h-[94px] min-h-[94px]" style={{ gridArea: 'metrics-top' }}>
                                <SingleMetric
                                    title={<div>
                                        <div className="text-12 text-sf-gray-700 font-sf">Protected</div>
                                        <div className="text-12 text-sf-gray-700 font-sf">users</div>
                                    </div>}
                                    value={protectedUsers ?? 0}
                                    isLoading={protectedUsersIsLoading}
                                    iconRight={Icons.ArrowTrendUp}
                                    iconClassName="text-sf-gray-400"
                                    className="text-21"
                                />
                            </div>
                            {/* Authentication Deny Rate Metric Card */}
                            <div className="h-[94px] min-h-[94px]" style={{ gridArea: 'denied-rate' }}>
                                <SingleMetric
                                    title={<div>
                                        <div className="text-12 text-sf-gray-700 font-sf">Authentication</div>
                                        <div className="text-12 text-sf-gray-700 font-sf">deny rate</div>
                                    </div>}
                                    value={Number(authenticationDenyRate?.toFixed(2)) ?? 0}
                                    iconRight={Icons.ArrowTrendUp}
                                    iconClassName="text-sf-gray-400"
                                    className="text-21"
                                />
                            </div>
                            {/* Authentications Denied Card */}
                            <div className="h-[94px] min-h-[94px]" style={{ gridArea: 'denied-count' }}>
                                <LayoutCarousel
                                    title="Authentications denied"
                                    contentClassName="py-2 flex flex-1 flex-col justify-center [&]:items-start group"
                                    className={blinkChartContainerClassName}
                                    pages={
                                        <div className="flex flex-row gap-2 px-2 w-full">
                                            {authenticationsDeniedIsLoading ? (
                                                <div className="h-6 w-[112px] animate-pulse rounded-sm bg-sf-gray-50"></div>
                                            ) : (
                                                <div className="flex flex-row justify-between w-full items-center">
                                                    <span className="[&]:text-21 font-bold font-sf-poppins">
                                                        {authenticationsDenied?.toLocaleString()}
                                                    </span>
                                                    <div>
                                                        <Tooltip title="View logs">
                                                            <IconButton
                                                                className="opacity-0 group-hover:opacity-100 cursor-pointer"
                                                                sizeVariant={IconButtonSizes.Large}
                                                                buttonStyle={IconButtonStyles.Primary}
                                                                iconSize={16}
                                                                icon={Icons.OpenInNew}
                                                                onClick={() => openLogsDrawer({ action: 'deny' })}
                                                            />
                                                        </Tooltip>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    }
                                />
                            </div>

                            {/* Policy type Chart */}
                            <div className="h-[200px] min-h-[200px]" style={{ gridArea: 'policies-bottom' }}>
                                <PolicyTypeChart
                                    policyType={policyType}
                                    policyTypeIsLoading={policyTypeIsLoading}
                                    policyTypeIsError={policyTypeIsError}
                                    selectedStatus={denyPolicyStatus}
                                />
                            </div>

                            {/* Authentication type Chart */}
                            <div className="h-[200px] min-h-[200px]" style={{ gridArea: 'auth-type' }}>
                                <AuthenticationTypeChart
                                    authenticationTypeSummary={authenticationTypeSummary}
                                    isLoading={authenticationTypeSummaryIsLoading}
                                    className={blinkChartContainerClassName}
                                    onClick={(authenticationType) =>
                                        openLogsDrawer({ directoryType: [authenticationType] })
                                    }
                                    innerCircleEmptyStateClassName={blinkChartContainerClassName}
                                />
                            </div>

                            {/* Protocol type Chart */}
                            <div className="h-[200px] min-h-[200px]" style={{ gridArea: 'protocol-type' }}>
                                <ProtocolTypeChart
                                    protocolTypeSummary={protocolTypeSummary}
                                    isLoading={protocolTypeSummaryIsLoading}
                                    className={blinkChartContainerClassName}
                                    onClick={(protocolType) => openLogsDrawer({ protocol: protocolType })}
                                    innerCircleEmptyStateClassName={blinkChartContainerClassName}
                                />
                            </div>

                            {/* Authentication Traffic Chart */}
                            <div
                                className="h-[380px] min-h-[380px]"
                                style={{ gridArea: 'traffic' }}
                                ref={chartContainerRef}>
                                <AuthenticationsTrafficLayout
                                    selectedTab={authenticationsTrafficSelectedTab}
                                    className={blinkChartContainerClassName}
                                    onTabChange={(tab) => setAuthenticationsTrafficSelectedTab(tab)}>
                                    <BarChart
                                        data={barChartData}
                                        isLoading={barChartIsLoading}
                                        primaryColor="#1C89AC"
                                        gradientEndColor="#C9DDE3"
                                        variant={barChartVariant}
                                        width={chartWidth}
                                        XAxisTickStyle={{
                                            fontSize: 10,
                                            color: '#808080'
                                        }}
                                        YAxisTickStyle={{
                                            fontSize: 10,
                                            color: '#808080'
                                        }}
                                        mappingList={mappingList}
                                        formatXAxisTick={
                                            barChartData.length >= AUTH_TRAFFIC_LABELS_LENGTH_THRESHOLD
                                                ? formatAuthTrafficShortDates
                                                : undefined
                                        }
                                        height={252}
                                    />
                                </AuthenticationsTrafficLayout>
                            </div>

                            {/* Separator */}
                            <div className="w-full flex items-center" style={{ gridArea: 'separator' }}>
                                <div className="px-2 h-6 flex items-center gap-1">
                                    <Icon icon={Icons.Refresh} size={16} />
                                    <span className="font-sf text-12 font-medium text-sf-gray-700">Last 24h</span>
                                </div>
                                <div className="flex-1 border-b border-sf-gray-300" />
                            </div>

                            {/* Top Active Chart */}
                            <div className="h-full min-h-[200px] w-full" style={{ gridArea: 'active' }}>
                                <TopActiveChart
                                    filters={filters}
                                    onInvestigationClick={handleInvestigationClick}
                                    activeUsers={activeUsers}
                                    activeResources={activeResources}
                                    activeUsersIsLoading={activeUsersIsLoading}
                                    activeResourcesIsLoading={activeResourcesIsLoading}
                                />
                            </div>

                            {/* Top Block Chart */}
                            <div className="h-full min-h-[200px] w-full" style={{ gridArea: 'blocked' }}>
                                <TopBlockChart
                                    filters={filters}
                                    onInvestigationClick={handleInvestigationClick}
                                    blockedUsers={blockedUsers}
                                    blockedResources={blockedResources}
                                    blockedUsersIsLoading={blockedUsersIsLoading}
                                    blockedResourcesIsLoading={blockedResourcesIsLoading}
                                />
                            </div>

                            {/* Top Risky Users */}
                            <div className="h-[200px] min-h-[200px]" style={{ gridArea: 'risk-users' }}>
                                <TopRiskEntityChart
                                    title="Top risky users"
                                    list={riskyUsers}
                                    isLoading={riskyUsersIsLoading}
                                    showAvatar={true}
                                    onInvestigationClick={handleUsersInvestigation}
                                />
                            </div>

                            {/* Top Risky Resources */}
                            <div className="h-[200px] min-h-[200px]" style={{ gridArea: 'risk-resources' }}>
                                <TopRiskEntityChart
                                    title="Top risky resources"
                                    list={riskyResources}
                                    isLoading={riskyResourcesIsLoading}
                                    onInvestigationClick={handleResourcesInvestigation}
                                />
                            </div>

                            {/* Top Risk Indicators */}
                            <div className="h-[200px] min-h-[200px]" style={{ gridArea: 'risk-indicators' }}>
                                <TopRiskIndicatorChart
                                    title="Top risk indicators"
                                    list={riskyIndicators}
                                    isLoading={riskyIndicatorsIsLoading}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <Insights />
            </div>
            <Alert />
        </>
    );
};

export default AuthenticationFirewallDashboard;
